// Cours premium n°4 — "Conduire • Anticiper • Choisir • Sécuriser • Être autonome"
// Circulation A2 — 14 chapitres (sans visuels, trame identique aux cours 1/2/3)
// Cours 1 : je découvre → Cours 2 : j'observe et anticipe → Cours 3 : je construis ma conduite
// → Cours 4 : je conduis de manière autonome et proche du niveau examen

export const cours = {
  slug: 'quatrieme-circulation-a2',
  title: 'Ma 4ᵉ circulation A2',
  subtitle: 'Conduire • Anticiper • Choisir • Sécuriser • Être autonome',
  duree: '2 heures de conduite',
  mantra: 'OBSERVER → COMPRENDRE → CRÉER DE LA MARGE → DÉCIDER → AGIR',
  intro: "Après trois séances consacrées à construire tes compétences, cette 4ᵉ séance de circulation change de logique : tu commences à conduire de manière globale, autonome, et proche des situations d'examen — sans transformer la leçon en examen blanc. À titre de repère, le minimum réglementaire pour le permis A2 est de 20 h de formation pratique, dont 8 h hors circulation et 12 h en circulation. Avec des séances de 2 h en circulation, la session 4 se situe aux deux tiers de ce volume minimal : ce n'est plus le moment de découvrir les fondamentaux, mais d'observer leur stabilité et leur transfert dans des situations nouvelles.",
  sections: [
    {
      num: '01',
      kicker: 'Point de départ',
      title: 'Ton bilan n°3 devient ton nouveau point de départ',
      lead: 'Tu repars de ce que tu as identifié lors de la séance précédente. Cette fois, ton enseignant intervient beaucoup moins sur ce que tu sais déjà faire.',
      type: 'timeline',
      steps: [
        { time: 'Acquis', label: 'CE QUE TU MAÎTRISES', desc: 'Tu identifies les compétences désormais solides et disponibles à tout moment.' },
        { time: 'Fragile', label: 'CE QUE TU DOIS CONSOLIDER', desc: 'Tu repères ce qui demande encore de l\'attention ou de la répétition.' },
        { time: 'Cible', label: 'TON OBJECTIF PERSONNEL', desc: 'Tu définis ce sur quoi tu vas concentrer ton énergie aujourd\'hui.' },
      ],
      retenir: 'CETTE FOIS, TON ENSEIGNANT INTERVIENT MOINS SUR CE QUE TU SAIS DÉJÀ FAIRE.',
    },
    {
      num: '02',
      kicker: 'Responsabilité',
      title: 'Tu prends la responsabilité de ta conduite',
      lead: 'Tu ne conduis plus en attendant une instruction. La radio peut t\'indiquer une destination ou une direction, mais c\'est toi qui construis ta conduite.',
      type: 'cards3',
      cards: [
        { title: 'TU OBSERVES', desc: 'Tu vas chercher activement les informations autour de toi, loin devant.' },
        { title: 'TU ANALYSES', desc: 'Tu interprètes ce que tu vois et tu identifies les risques.' },
        { title: 'TU DÉCIDES & TU AGIS', desc: 'Tu choisis ta réponse, puis tu la mets en œuvre avec précision.' },
      ],
      retenir: 'LA RADIO T\'INDIQUE OÙ ALLER. ELLE NE TE DIT PLUS COMMENT CONDUIRE.',
    },
    {
      num: '03',
      kicker: 'Autonomie continue',
      title: 'Tu conduis avec plus d\'autonomie',
      lead: 'Nous augmentons progressivement les périodes pendant lesquelles ton enseignant n\'intervient pas. Tu dois maintenir seul, sur la durée :',
      type: 'list',
      list: [
        { label: 'OBSERVATION', desc: 'Ton regard reste actif et anticipe, sans rappel extérieur.' },
        { label: 'ALLURE', desc: 'Adaptée en permanence à la route et au trafic.' },
        { label: 'POSITIONNEMENT', desc: 'Choisi sur la voie et dans la circulation, sans consigne.' },
        { label: 'DISTANCES', desc: 'Maintenues devant, derrière et sur les côtés.' },
        { label: 'COMMUNICATION', desc: 'Clignotants, regards et signaux clairs et anticipés.' },
        { label: 'ANTICIPATION', desc: 'Tu prépares ce qui arrive avant d\'y être confronté.' },
      ],
      retenir: 'NE PAS AVOIR BESOIN D\'UNE CONSIGNE POUR BIEN CONDUIRE.',
    },
    {
      num: '04',
      kicker: 'Suivi de direction',
      title: 'Tu suis une direction en autonomie',
      lead: 'Nous nous rapprochons ici d\'une compétence réellement évaluée à l\'examen. Ton enseignant peut te demander : « Suis maintenant la direction Grenoble. » À toi de rechercher la signalisation et de construire ton itinéraire tout en continuant à conduire.',
      type: 'cards3',
      cards: [
        { title: 'JE CHERCHE', desc: 'Je repère les panneaux et la signalisation directionnelle.' },
        { title: 'JE CONSTRUIS', desc: 'Je prépare mon itinéraire pendant que je roule.' },
        { title: 'JE CONDUIS', desc: 'Ma priorité reste la conduite, pas la recherche du chemin.' },
      ],
      body: "À l'examen A2, tu dois notamment démontrer ton autonomie et suivre pendant environ 5 minutes un itinéraire ou une destination préalablement établie.",
      retenir: 'TE TROMPER DE DIRECTION N\'EST PAS LE PROBLÈME. TE METTRE EN DANGER POUR RATTRAPER TON ERREUR EN EST UN.',
    },
    {
      num: '05',
      kicker: 'Changements d\'environnement',
      title: 'Tu gères les changements d\'environnement',
      lead: 'À chaque transition, pose-toi la question : « Qu\'est-ce qui vient de changer et qu\'est-ce que je dois adapter ? » Regard, allure, distances, positionnement et anticipation évoluent avec l\'environnement.',
      type: 'timeline',
      steps: [
        { time: '1', label: 'VILLE → ROUTE', desc: 'Allure supérieure, distances plus grandes, regard plus lointain.' },
        { time: '2', label: 'ROUTE → VOIE RAPIDE', desc: 'Insertion, cohabitation avec des véhicules rapides, contrôles renforcés.' },
        { time: '3', label: 'VOIE RAPIDE → AGGLOMÉRATION', desc: 'Réduction franche de l\'allure, multiplication des usagers vulnérables.' },
        { time: '4', label: 'FLUIDE → DENSE', desc: 'Espaces plus étroits, communication et distances remaniées en continu.' },
      ],
      retenir: 'À CHAQUE TRANSITION : QU\'EST-CE QUI CHANGE ? QU\'EST-CE QUE JE DOIS ADAPTER ?',
    },
    {
      num: '06',
      kicker: 'Planification',
      title: 'Tu planifies plusieurs actions à l\'avance',
      lead: 'Tu aperçois un giratoire au loin : tu ne dois pas attendre d\'y arriver pour commencer à le gérer. Tu prépares ta séquence complète, dans l\'ordre :',
      type: 'list',
      list: [
        { label: 'DIRECTION', desc: 'Je choisis ma sortie avant d\'arriver.' },
        { label: 'CONTRÔLES', desc: 'Rétros, angle mort, arrière.' },
        { label: 'ALLURE', desc: 'Je ralentis progressivement pour rester libre de mes options.' },
        { label: 'COMMUNICATION', desc: 'Clignotant clair et anticipé.' },
        { label: 'POSITIONNEMENT', desc: 'Je me place sur la voie qui prépare ma sortie.' },
        { label: 'PRIORITÉS', desc: 'J\'identifie qui a la priorité et ce qui peut arriver.' },
        { label: 'ENGAGEMENT', desc: 'Je franchis en ayant déjà anticipé la sortie.' },
      ],
      retenir: 'NOUS CHERCHONS UNE CONDUITE PRÉPARÉE, ET NON UNE SUCCESSION DE RÉACTIONS.',
    },
    {
      num: '07',
      kicker: 'Imprévu',
      title: 'Tu gères l\'imprévu sans te précipiter',
      lead: 'Un véhicule freine. Un piéton apparaît. Une voiture débouche. Un véhicule masque ta visibilité. Une voie se termine. Ton premier réflexe n\'est pas forcément d\'agir brutalement.',
      type: 'list',
      list: [
        { label: 'OBSERVER', desc: 'Je prends l\'information brute avant de juger.' },
        { label: 'COMPRENDRE', desc: 'J\'analyse la situation et le risque réel.' },
        { label: 'CRÉER DE LA MARGE', desc: 'Je gagne du temps et de l\'espace pour choisir.' },
        { label: 'DÉCIDER', desc: 'Je choisis la réponse la plus sûre, pas la plus impulsive.' },
        { label: 'AGIR', desc: 'Je passe à l\'action de manière progressive et contrôlée.' },
      ],
      retenir: 'OBSERVER → COMPRENDRE → CRÉER DE LA MARGE → DÉCIDER → AGIR.',
    },
    {
      num: '08',
      kicker: 'Renoncement',
      title: 'Tu sais renoncer',
      lead: 'C\'est une compétence majeure du motard autonome. Changer de décision n\'est pas un échec : c\'est le signe que tu observes et que tu adaptes.',
      type: 'cards3',
      cards: [
        { title: 'DÉPASSEMENT', desc: 'Tu avais prévu de dépasser mais la visibilité diminue ? Tu renonces.' },
        { title: 'INSERTION', desc: 'Tu voulais t\'insérer mais l\'espace devient insuffisant ? Tu attends.' },
        { title: 'DOUTE', desc: 'Tu pensais passer mais un doute apparaît ? Tu sécurises.' },
      ],
      retenir: 'CHANGER DE DÉCISION N\'EST PAS ÉCHOUER. C\'EST S\'ADAPTER.',
    },
    {
      num: '09',
      kicker: 'Partage',
      title: 'Tu partages la route',
      lead: 'Ton autonomie ne concerne pas uniquement ta moto. Tu recherches une conduite qui permette de circuler sans gêner, sans surprendre et sans être surpris.',
      type: 'list',
      list: [
        { label: 'PIÉTONS', desc: 'Priorité absolue lorsqu\'ils traversent ou s\'apprêtent à le faire.' },
        { label: 'CYCLISTES & EDPM', desc: 'Distances latérales suffisantes, dépassement anticipé, patience.' },
        { label: 'POIDS LOURDS', desc: 'Angles morts importants : je me rends visible et je garde mes distances.' },
        { label: 'VOITURES', desc: 'Je reste lisible, je communique tôt, je ne surprends pas.' },
        { label: 'TRANSPORTS EN COMMUN', desc: 'Arrêts, redémarrages et voies dédiées : je m\'adapte.' },
        { label: 'AUTRES MOTOS', desc: 'Je partage la voie sans imposer ma trajectoire.' },
      ],
      retenir: 'CIRCULER SANS GÊNER. SANS SURPRENDRE. SANS ÊTRE SURPRIS.',
    },
    {
      num: '10',
      kicker: 'Courtoisie',
      title: 'Tu montres une conduite préventive et courtoise',
      lead: "Faciliter une insertion lorsque c'est possible, conserver des distances, ne pas imposer sa priorité, prendre particulièrement en compte les usagers vulnérables… Cette attitude préventive et courtoise fait explicitement partie de l'évaluation de l'examen circulation A2.",
      type: 'cards3',
      cards: [
        { title: 'LISIBLE', desc: 'Mes intentions sont claires et compréhensibles par les autres.' },
        { title: 'PRÉVISIBLE', desc: 'Mes actions arrivent au bon moment, sans surprise.' },
        { title: 'APAISÉE', desc: 'Je ne cherche pas à imposer, je facilite la circulation.' },
      ],
      retenir: 'LA CONDUITE DOIT DEVENIR LISIBLE, PRÉVISIBLE ET APAISÉE.',
    },
    {
      num: '11',
      kicker: 'Régularité',
      title: 'Tu maintiens ton niveau dans la durée',
      lead: 'Faire correctement une intersection ne suffit plus. Nous cherchons maintenant la régularité, sur plusieurs kilomètres.',
      type: 'list',
      list: [
        { label: 'REGARD LOIN', desc: 'Toujours actif, pas seulement en début de séance.' },
        { label: 'CONTRÔLES RÉGULIERS', desc: 'Rétros et angles morts intégrés au rythme naturel de la conduite.' },
        { label: 'ALLURE COHÉRENTE', desc: 'Adaptée sans à-coups à chaque environnement.' },
        { label: 'BONNES DISTANCES', desc: 'Maintenues en toute circonstance, y compris quand le trafic se densifie.' },
        { label: 'DÉCISIONS ADAPTÉES', desc: 'Justes, au bon moment, même lorsque la fatigue arrive.' },
        { label: 'CONCENTRATION', desc: 'Stable sur toute la durée du trajet.' },
      ],
      retenir: 'NE PAS SEULEMENT SAVOIR FAIRE. SAVOIR LE FAIRE DURABLEMENT.',
    },
    {
      num: '12',
      kicker: 'Regard d\'examinateur',
      title: 'Ton enseignant t\'observe comme un motard',
      lead: "Nous pouvons maintenant intégrer des séquences proches de l'évaluation : moins d'interventions radio, itinéraire varié, autonomie et situations réelles. Ton enseignant observe ce que l'examinateur observera le jour J.",
      type: 'list',
      list: [
        { label: 'PRISE D\'INFORMATION', desc: 'Regard actif, contrôles pertinents, indices exploités.' },
        { label: 'ALLURE', desc: 'Adaptée au contexte, aux limitations et à la visibilité.' },
        { label: 'RÉGLEMENTATION', desc: 'Signalisation, priorités, règles de circulation respectées.' },
        { label: 'COMMUNICATION', desc: 'Clignotants, position, regards : tu te fais comprendre.' },
        { label: 'PARTAGE DE LA CHAUSSÉE', desc: 'Tu tiens compte des autres usagers en permanence.' },
        { label: 'ESPACES DE SÉCURITÉ', desc: 'Distances devant, derrière et sur les côtés préservées.' },
        { label: 'ANALYSE & ADAPTATION', desc: 'Tu lis la situation et tu ajustes ta conduite.' },
        { label: 'AUTONOMIE', desc: 'Tu conduis seul, sans avoir besoin d\'être guidé pas à pas.' },
      ],
      retenir: 'CE SONT PRÉCISÉMENT LES DOMAINES ÉVALUÉS LE JOUR DE L\'ÉPREUVE A2.',
    },
    {
      num: '13',
      kicker: 'Auto-analyse',
      title: 'Tu analyses toi-même ta conduite',
      lead: "À l'arrêt pédagogique, avant que ton enseignant te donne son avis : « Comment évalues-tu cette partie du trajet ? » On inverse progressivement les rôles.",
      type: 'list',
      list: [
        { label: '« QU\'AS-TU BIEN FAIT ? »', desc: 'Tu identifies les points positifs concrets de ta conduite.' },
        { label: '« QUEL RISQUE AS-TU IDENTIFIÉ ? »', desc: 'Tu nommes les situations que tu as anticipées.' },
        { label: '« QUELLE DÉCISION REFERAIS-TU ? »', desc: 'Tu valides ce qui a été juste au bon moment.' },
        { label: '« QUE MODIFIERAIS-TU ? »', desc: 'Tu proposes toi-même une amélioration.' },
      ],
      retenir: 'C\'EST TOI QUI COMMENCES PAR ANALYSER TA CONDUITE.',
    },
    {
      num: '14',
      kicker: 'Bilan N°4',
      title: 'Ton bilan n°4 : suis-je capable de conduire sans être guidé ?',
      lead: "L'auto-évaluation devient plus exigeante. Est-ce que je sais…",
      type: 'bilan',
      questions: [
        'Conduire plusieurs minutes sans intervention ?',
        'Suivre une direction tout en restant concentré sur ma conduite ?',
        'Détecter seul un risque ?',
        'Anticiper plusieurs actions ?',
        'Adapter ma conduite lorsqu\'un environnement change ?',
        'Gérer un imprévu sans précipitation ?',
        'Renoncer lorsque les conditions ne sont plus favorables ?',
        'Maintenir mes marges de sécurité ?',
        'Partager la route avec les autres ?',
        'Conserver une conduite régulière dans la durée ?',
        'Expliquer et corriger moi-même une erreur ?',
      ],
      blocks: [
        { title: 'JE MAÎTRISE', color: 'green' },
        { title: 'JE CONSOLIDE', color: 'amber' },
        { title: 'OBJECTIF SESSION N°5', color: 'red' },
      ],
      retenir: 'L\'AUTONOMIE, CE N\'EST PAS CONDUIRE SANS ENSEIGNANT. C\'EST SAVOIR PRENDRE SEUL UNE DÉCISION SÛRE.',
    },
  ],
};
